'use client';

import { EPlatform } from '@/utils/enums/platform-enum';
import { useState, useRef, useEffect } from 'react';
import { CollapseArrowDownOutlined } from '@zeelool/icon';
import './SparklySeoInfo.css';
import logger from '@/utils/client-logger';

interface SparklySeoInfoProps {
  groupInfo: {
    h_one?: string;
    pc_pure_text?: string;
  };
  deviceType: EPlatform;
}

export default function SparklySeoInfo({ groupInfo, deviceType }: SparklySeoInfoProps) {
  const [isHideText, setIsHideText] = useState(false);
  const [isOverTwo, setIsOverTwo] = useState(false);
  const [textHeight, setTextHeight] = useState('210px');
  const seoDescRef = useRef<HTMLDivElement>(null);
  const isWAP = deviceType !== EPlatform.WEB;

  // 计算文本高度逻辑（完全按照Vue版本）
  const calculateText = () => {
    // 这是默认两行数据的高度，一行的高度可以自定义
    const twoHeight = 21 * 10; // 现按照这个高度确定，不按照几行确定
    const calculatedTextHeight = `${twoHeight}px`;
    setTextHeight(calculatedTextHeight);

    try {
      if (seoDescRef.current) {
        const curHeight = seoDescRef.current.clientHeight;
        if (curHeight > twoHeight) {
          setIsHideText(true);
          setIsOverTwo(true);
        } else {
          setIsHideText(false);
          setIsOverTwo(false);
        }
      }
    } catch (error) {
      // Error handling - silently continue
      logger.error('Error calculating text height:', error);
    }
  };

  useEffect(() => {
    if (groupInfo.pc_pure_text) {
      // 设置初始状态为false（Vue版本的逻辑）
      setIsHideText(false);
      // 下一帧计算高度
      setTimeout(() => {
        calculateText();
      }, 0);
    }
  }, [groupInfo.pc_pure_text]);

  const handleToggleText = () => {
    setIsHideText(!isHideText);
  };

  if (!groupInfo.h_one && !groupInfo.pc_pure_text) {
    return null;
  }

  return (
    <div
      className={`
      seo-info bg-white ${isWAP ? 'wap' : 'pc'}
      ${
        isWAP
          ? 'px-[12px] pb-[40px]'
          : 'py-[32px] px-[24px] mx-[50px] mb-[56px] text-center rounded-[8px]'
      }
    `}
    >
      {groupInfo.h_one && (
        <h1
          className={`
          text-left font-bb text-[#282828]
          ${isWAP ? 'text-[16px] leading-[22px]' : 'text-[24px] mb-[15px]'}
        `}
        >
          {groupInfo.h_one}
        </h1>
      )}

      {groupInfo.pc_pure_text && (
        <div className="desc-box text-[14px] leading-[22px] relative">
          <div
            ref={seoDescRef}
            className={`
              desc-detail text-left whitespace-pre-wrap
              ${isWAP ? 'text-[12px] leading-[18px]' : 'text-[14px] leading-[22px]'}
              ${isHideText ? 'statusText overflow-hidden block' : ''}
            `}
            style={{
              maxHeight: isHideText ? textHeight : undefined,
            }}
            dangerouslySetInnerHTML={{ __html: groupInfo.pc_pure_text }}
          />
        </div>
      )}

      {/* 阴影层 - 按照Vue版本放在desc-box外面，仅PC端显示 */}
      {!isWAP && <div className={isHideText ? 'shadow' : ''} />}

      {/* 展开/收起按钮 - 双端统一样式 */}
      {isOverTwo && (
        <div className="seo-btn mt-[25px]">
          <p
            className="
              flex items-center justify-center w-[44px] h-[20px] mx-auto cursor-pointer
              bg-white rounded-[10px] border border-[#d0d4db]
            "
            onClick={handleToggleText}
          >
            <CollapseArrowDownOutlined
              className={`
                text-[#282828] text-[16px] transition-transform duration-300
                ${!isHideText ? 'rotate-180' : 'rotate-0'}
              `}
            />
          </p>
        </div>
      )}
    </div>
  );
}
